// Der Netzwerk-Block (Cluster, OpenWrt-Router, Verbindungen, Topologie, Rekey-
// Fenster, Integrationen) -- EIN gemeinsamer Stand für network-summary,
// network-details, topology-summary, link-state, rekey-window und integrations.
//
// Datenfluss (seit 02.11.2026 über das Homelab-Protokoll, docs/protokoll.md
// › „Netzwerk über das Protokoll“): jeder Cluster ist eine Instanz
// netz.<id>.cluster, jeder OpenWrt-Router openwrt.<id>.stand, jede Schnittstelle
// link.<id>.stand, jeder Knoten der Topologie topologie.<id>.knoten; die Kanten
// stehen zusammen in topologie.kanten, offene Rekey-Fenster und Integrationen in
// netz.lage. Das Backend gleicht die Quellen ab (Collector, Agenten-Cache,
// link_state, Topologie, Rekey, Integrationen); der Hub verwirft Unverändertes
// und schickt Deltas. Bis dahin holte jeder der sechs Stores seine Liste in
// festen Takten (15 bis 30 s) per REST und zusätzlich bei jedem WS-Signal
// ("network", "openwrt", "events") -- je Tab, je Store; network-summary und
// network-details holten dieselbe Topologie sogar doppelt.
//   - Hier stehen die einzigen Abo-Stellen (`netz.*.cluster`, `openwrt.*.stand`,
//     `link.*.stand`, `topologie.*.knoten`, `topologie.kanten`, `netz.lage`).
//     Aus einem Stand baut lib/netz-protokoll.ts die REST-Form seiner Quelle
//     (`NetworkTopology`, `OpenwrtSnapshot`, `LinkState`, `TopoNode`, der Graph,
//     `RekeyWindow`, `Integration`). Die Summen der Karten (Geräte online,
//     Topologie, Verbindungen) rechnen die dünnen Sichten daraus, nur bei
//     geänderter Eingabe neu.
//   - REST (die sechs Endpunkte, je für sich) nur beim Start, als Revalidator
//     (Reconnect, Tab kommt zurück) und als Neuversuch 15 s nach einem
//     Fehlschlag -- jeweils nur die gescheiterten Teile --, dazu in refreshNetz
//     (nach einer Aktion), das nur die genannten Teile holt. KEIN Takt, KEIN
//     Abo auf die alten WS-Kanäle "network", "openwrt" und "events" mehr. (Das
//     Präsenz-Abo "openwrt:live" der Seiten bleibt: es taktet den Router-Agenten
//     hoch, nicht diesen Store.)
//   - Stände und Abrufe mischen wie in agents-summary (ProtokollListe): Stände
//     in-place, ein Stand während eines Abrufs liegt über dessen Antwort, ein
//     `weg` hinterlässt einen Grabstein. Die Reihenfolge der Cluster und
//     Knoten steht in `position`. Die Kanten und die Lage sind Einzelwerte
//     (growbox.lage): ein Stand während eines Abrufs liegt ebenfalls darüber.
//     Jeder Teil gilt für sich als bekannt (Ablage, Abruf oder Stand): vorher
//     liefert sein Getter `undefined`.
//
// Mehrere Stores teilen sich den Stand: startNetz() zählt mit und liefert die
// Abmeldung; der letzte, der geht, schließt ab.
//
// SWR: ja -- Cluster, Router, Verbindungen und Topologie kommen beim Start aus
// der Ablage (ein Schlüssel `netz`). Rekey-Fenster und Integrationen nicht:
// ein abgelaufenes Fenster dürfte nie aus der Ablage aufleuchten.

import { getJSON } from "./api";
import {
  clusterAusProtokoll,
  clusterIdAusInstanz,
  graphAusProtokoll,
  integrationenAusProtokoll,
  kantenAusProtokoll,
  knotenAusProtokoll,
  knotenIdAusInstanz,
  linkAusProtokoll,
  linkIdAusInstanz,
  positionAusStand,
  rekeyAusProtokoll,
  routerAusProtokoll,
  routerIdAusInstanz,
  vergleicheLinks,
  vergleicheNachPosition,
  vergleicheRouter,
  type Kanten,
} from "./netz-protokoll";
import type { Integration } from "./integrations.svelte";
import type { LinkState } from "./link-state.svelte";
import type { OpenwrtSnapshot } from "./network-details.svelte";
import { clearPersisted, loadPersisted, persist } from "./persistent-summary.svelte";
import { ProtokollListe } from "./protokoll-liste.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { LinkStand, NetzCluster, NetzLage, OpenwrtStand, TopologieKanten, TopologieKnoten } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import type { RekeyWindow } from "./rekey-window.svelte";
import { registerRevalidator } from "./revalidate.svelte";
import { gleich } from "./storage-protokoll";
import type { TopoGraph, TopoNode } from "./topology-summary.svelte";
import type { NetworkTopology } from "./types";

const STORAGE_KEY = "netz";

// Die Ablage-Schlüssel der alten Stores (bis 02.11.2026): sie werden nicht mehr
// geschrieben; startNetz räumt sie einmal je Sitzung weg.
const ALTE_SCHLUESSEL = ["network", "network-topology", "network-openwrt", "topology", "link-state"];
let _altesGeraeumt = false;

/** Die Ablage: ein Teil fehlt, solange er unbekannt war. */
interface Gespeichert {
  cluster?: NetworkTopology[];
  router?: OpenwrtSnapshot[];
  links?: LinkState[];
  knoten?: TopoNode[];
  kanten?: Kanten;
}

/** Die Teile des gemeinsamen Stands; jeder hat seinen Endpunkt. `graph` holt
 *  Knoten und Kanten in einem Abruf. */
export type NetzTeil = "cluster" | "router" | "link" | "graph" | "rekey" | "integrationen";
const ALLE_TEILE: NetzTeil[] = ["cluster", "router", "link", "graph", "rekey", "integrationen"];

/** Eine Liste aus Ständen: die Einträge (ProtokollListe), ihre Stelle in der
 *  Reihenfolge der Quelle, welche Instanz zu welchem Schlüssel gehört (`weg`
 *  nennt nur die Instanz) und ob sie etwas weiß (Ablage, Abruf oder Stand). */
class Bestand<T extends object> {
  readonly schluessel: (e: T) => string;
  readonly liste: ProtokollListe<T>;
  readonly pos = new Map<string, number>();
  readonly instanzen = new Map<string, string>();
  /** Ob die Liste etwas weiß: vorher liefert ihr Getter `undefined`. */
  bekannt = $state(false);

  constructor(
    schluessel: (e: T) => string,
    vergleiche: (position: (k: string) => number | undefined) => (a: T, b: T) => number,
  ) {
    this.schluessel = schluessel;
    this.liste = new ProtokollListe<T>(schluessel, vergleiche((k) => this.pos.get(k)));
  }

  /** Ein Stand einer Instanz (mit seiner Stelle, falls die Quelle eine führt).
   *  true, wenn sich die Liste geändert hat. */
  stand(instanz: string, neu: T, position?: number): boolean {
    const k = this.schluessel(neu);
    // Hat sich der Schlüssel der Instanz geändert (umbenannt), ist der alte Eintrag weg.
    const alt = this.instanzen.get(instanz);
    let geaendert = false;
    if (alt !== undefined && alt !== k) {
      this.pos.delete(alt);
      geaendert = this.liste.weg(alt);
    }
    this.instanzen.set(instanz, k);
    let stelle = false;
    if (position !== undefined && this.pos.get(k) !== position) {
      this.pos.set(k, position);
      stelle = true;
    }
    if (this.liste.stand(neu)) geaendert = true;
    // Die Stelle kann sich ändern, ohne dass sich der Eintrag ändert (ein
    // geänderter Eintrag ist schon eingeordnet).
    else if (stelle) this.liste.ordne();
    if (stelle) geaendert = true;
    // Vor dem ersten Abruf legt der Abruf die Einträge über die Antwort.
    if (this.liste.bereit) this.bekannt = true;
    return geaendert;
  }

  /** Eine Instanz ist weg. true, wenn die Liste ihren Eintrag führte. */
  weg(instanz: string): boolean {
    const k = this.instanzen.get(instanz);
    if (k === undefined) return false;
    this.instanzen.delete(instanz);
    this.pos.delete(k);
    return this.liste.weg(k);
  }

  /** Aus der Ablage: die Reihenfolge der Liste ist die der Quelle. */
  ausAblage(items: T[]): void {
    items.forEach((e, i) => this.pos.set(this.schluessel(e), i));
    this.liste.items = items;
    this.bekannt = true;
  }

  leere(): void {
    this.liste.leere();
    this.pos.clear();
    this.instanzen.clear();
    this.bekannt = false;
  }
}

const _cluster = new Bestand<NetworkTopology>((e) => e.id, (p) => vergleicheNachPosition((e: NetworkTopology) => e.id, p));
const _router = new Bestand<OpenwrtSnapshot>((e) => e.router ?? "", () => vergleicheRouter);
const _links = new Bestand<LinkState>((e) => e.entity, () => vergleicheLinks);
const _knoten = new Bestand<TopoNode>((e) => e.id, (p) => vergleicheNachPosition((e: TopoNode) => e.id, p));

// Die Einzelwerte: Kanten, offene Rekey-Fenster, Integrationen -- mit der
// laufenden Nummer ihres jüngsten Stands (kam er während eines Abrufs, liegt er
// über dessen Antwort). `undefined` = unbekannt.
let _kanten = $state.raw<Kanten | undefined>(undefined);
let _rekey = $state.raw<RekeyWindow[] | undefined>(undefined);
let _integrationen = $state.raw<Integration[] | undefined>(undefined);
let _kantenNr = 0;
let _rekeyNr = 0;
let _integrationenNr = 0;
let _nr = 0;

let _nutzer = 0;
let _unsub: Array<() => void> = [];

// ── Persistenz ───────────────────────────────────────────────────────

// Aus dem Protokoll kommt oft etwas (der Router-Agent meldet im 5-s-Takt):
// gespeichert wird gebündelt, höchstens alle 2 s.
let _persistTimer: ReturnType<typeof setTimeout> | undefined;
function persistSpaeter(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    const g: Gespeichert = {
      cluster: _cluster.bekannt ? $state.snapshot(_cluster.liste.items) : undefined,
      router: _router.bekannt ? $state.snapshot(_router.liste.items) : undefined,
      links: _links.bekannt ? $state.snapshot(_links.liste.items) : undefined,
      knoten: _knoten.bekannt ? $state.snapshot(_knoten.liste.items) : undefined,
      kanten: _kanten,
    };
    persist(STORAGE_KEY, g);
  }, 2_000);
}

// ── Stände ──────────────────────────────────────────────────────────

function beiCluster(instanz: string, daten: Record<string, unknown>): void {
  const id = clusterIdAusInstanz(instanz);
  if (id === undefined) return;
  const st = skaliere<NetzCluster>("netz.cluster", daten);
  if (_cluster.stand(instanz, clusterAusProtokoll(st, id), positionAusStand(st))) persistSpaeter();
}

function beiClusterWeg(instanz: string): void {
  if (_cluster.weg(instanz)) persistSpaeter();
}

function beiRouter(instanz: string, daten: Record<string, unknown>): void {
  const id = routerIdAusInstanz(instanz);
  if (id === undefined) return;
  const st = skaliere<OpenwrtStand>("openwrt.stand", daten);
  if (_router.stand(instanz, routerAusProtokoll(st, id))) persistSpaeter();
}

function beiRouterWeg(instanz: string): void {
  if (_router.weg(instanz)) persistSpaeter();
}

function beiLink(instanz: string, daten: Record<string, unknown>): void {
  if (linkIdAusInstanz(instanz) === undefined) return;
  const st = skaliere<LinkStand>("link.stand", daten);
  if (_links.stand(instanz, linkAusProtokoll(st))) persistSpaeter();
}

function beiLinkWeg(instanz: string): void {
  if (_links.weg(instanz)) persistSpaeter();
}

function beiKnoten(instanz: string, daten: Record<string, unknown>): void {
  if (knotenIdAusInstanz(instanz) === undefined) return;
  const st = skaliere<TopologieKnoten>("topologie.knoten", daten);
  if (_knoten.stand(instanz, knotenAusProtokoll(st), positionAusStand(st))) persistSpaeter();
}

function beiKnotenWeg(instanz: string): void {
  if (_knoten.weg(instanz)) persistSpaeter();
}

function beiKanten(_instanz: string, daten: Record<string, unknown>): void {
  const k = kantenAusProtokoll(skaliere<TopologieKanten>("topologie.kanten", daten));
  _kantenNr = ++_nr;
  if (_kanten && gleich(_kanten, k)) return;
  _kanten = k;
  persistSpaeter();
}

function beiLage(_instanz: string, daten: Record<string, unknown>): void {
  const l = skaliere<NetzLage>("netz.lage", daten);
  const nr = ++_nr;
  _rekeyNr = nr;
  _integrationenNr = nr;
  setzeRekey(rekeyAusProtokoll(l));
  setzeIntegrationen(integrationenAusProtokoll(l));
}

// Einzelwerte nur setzen, wenn sie sich ändern (sonst meldet jeder Stand ein Update).
function setzeRekey(neu: RekeyWindow[]): void {
  if (!_rekey || !gleich(_rekey, neu)) _rekey = neu;
}

function setzeIntegrationen(neu: Integration[]): void {
  if (!_integrationen || !gleich(_integrationen, neu)) _integrationen = neu;
}

// ── REST ─────────────────────────────────────────────────────────────

// Nach einem gescheiterten Abruf ein neuer Versuch: ohne Takt käme sonst erst
// der nächste Revalidator -- und bis dahin bliebe ein aus der Ablage geholter,
// inzwischen verschwundener Eintrag stehen (für den kommt kein `weg`, diese
// Sitzung kannte seine Instanz nie).
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;
// Die Teile, deren letzter Abruf scheiterte: nur sie versucht der Neuversuch.
const _gescheitert = new Set<NetzTeil>();

// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach dem Stopp
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

/** Die Antwort eines Endpunkts, `undefined` bei Fehlschlag (eine leere Antwort ist eine leere Liste). */
async function hole<T>(pfad: string): Promise<T[] | undefined> {
  try {
    return (await getJSON<T[] | null>(pfad)) ?? [];
  } catch {
    return undefined;
  }
}

// Eine Liste aus ihrer Antwort mischen (`rest` fehlt, wenn der Abruf scheiterte;
// was das Protokoll währenddessen brachte, gilt trotzdem -- beim ersten Abruf
// wären die Listen sonst leer, bis jeder Eintrag sich ändert).
function mische<T extends object>(b: Bestand<T>, rest: T[] | undefined, ab: number): void {
  try {
    if (b.liste.mische(rest, ab)) persistSpaeter();
    if (rest || b.liste.neuer(ab)) b.bekannt = true;
  } finally {
    b.liste.bereit = true;
  }
}

// Je Teil: Abruf, Einmischen. Liefert, ob der Abruf gelang.
async function ladeCluster(lauf: number): Promise<boolean> {
  const ab = _cluster.liste.nr;
  const rest = await hole<NetworkTopology>("/v2-api/network/topology");
  if (lauf !== _lauf) return true;
  rest?.forEach((c, i) => {
    if (!_cluster.liste.jungerAls(c.id, ab)) _cluster.pos.set(c.id, i);
  });
  mische(_cluster, rest, ab);
  return rest !== undefined;
}

async function ladeRouter(lauf: number): Promise<boolean> {
  const ab = _router.liste.nr;
  const rest = await hole<OpenwrtSnapshot>("/v2-api/openwrt/agents");
  if (lauf !== _lauf) return true;
  mische(_router, rest, ab);
  return rest !== undefined;
}

async function ladeLinks(lauf: number): Promise<boolean> {
  const ab = _links.liste.nr;
  const rest = await hole<LinkState>("/v2-api/link-state");
  if (lauf !== _lauf) return true;
  mische(_links, rest, ab);
  return rest !== undefined;
}

async function ladeGraph(lauf: number): Promise<boolean> {
  const ab = _knoten.liste.nr;
  const abKanten = _kantenNr;
  let graph: TopoGraph | undefined;
  try {
    graph = await getJSON<TopoGraph>("/v2-api/topology");
  } catch {
    graph = undefined;
  }
  if (lauf !== _lauf) return true;
  if (graph) {
    // Die Reihenfolge der Antwort ist die der Quelle; ein Stand, der während des
    // Abrufs kam, kennt seine Stelle selbst.
    (graph.nodes ?? []).forEach((n, i) => {
      if (!_knoten.liste.jungerAls(n.id, ab)) _knoten.pos.set(n.id, i);
    });
    // Kanten: ein Stand während des Abrufs liegt darüber.
    if (_kantenNr <= abKanten) {
      const k: Kanten = { edges: graph.edges ?? [], check_edges: graph.check_edges ?? [] };
      if (!_kanten || !gleich(_kanten, k)) {
        _kanten = k;
        persistSpaeter();
      }
    }
  }
  // Knoten speichert mische, wenn sich die Liste geändert hat.
  mische(_knoten, graph ? (graph.nodes ?? []) : undefined, ab);
  return graph !== undefined;
}

async function ladeRekey(lauf: number): Promise<boolean> {
  const ab = _rekeyNr;
  const rest = await hole<RekeyWindow>("/v2-api/security/rekey-window");
  if (lauf !== _lauf) return true;
  if (rest && _rekeyNr <= ab) setzeRekey(rest);
  return rest !== undefined;
}

async function ladeIntegrationen(lauf: number): Promise<boolean> {
  const ab = _integrationenNr;
  const rest = await hole<Integration>("/v2-api/integrations");
  if (lauf !== _lauf) return true;
  if (rest && _integrationenNr <= ab) setzeIntegrationen(rest);
  return rest !== undefined;
}

const _laden: Record<NetzTeil, (lauf: number) => Promise<boolean>> = {
  cluster: ladeCluster,
  router: ladeRouter,
  link: ladeLinks,
  graph: ladeGraph,
  rekey: ladeRekey,
  integrationen: ladeIntegrationen,
};

// Holt die genannten Teile (Standard: alle) und mischt jeden für sich.
async function ladeTeile(teile: NetzTeil[] = ALLE_TEILE): Promise<void> {
  const lauf = _lauf;
  // Die Teile getrennt: scheitert einer, gelten die anderen trotzdem.
  const antworten = await Promise.allSettled(teile.map((t) => _laden[t](lauf)));
  if (lauf !== _lauf) return;
  teile.forEach((t, i) => {
    const a = antworten[i];
    if (a.status === "fulfilled" && a.value) _gescheitert.delete(t);
    else _gescheitert.add(t);
  });
  if (_gescheitert.size > 0 && _nutzer > 0 && !_neuversuch) {
    _neuversuch = setTimeout(() => {
      _neuversuch = undefined;
      if (_nutzer > 0) void ladeTeile([..._gescheitert]);
    }, NEUVERSUCH_MS);
  }
}

/** Meldet einen Nutzer des gemeinsamen Stands an (der erste startet Abo und
 *  Abruf) und liefert die Abmeldung (der letzte schließt ab). */
export function startNetz(): () => void {
  _nutzer++;
  if (_nutzer === 1) {
    _lauf++;
    // SWR: gecachte Listen sofort einspielen. Das Protokoll bringt die frischen
    // Werte danach in Sekunden, der Cache deckt nur die First-Paint-Lücke.
    if (!_altesGeraeumt) {
      _altesGeraeumt = true;
      for (const k of ALTE_SCHLUESSEL) clearPersisted(k);
    }
    const cached = loadPersisted<Gespeichert>(STORAGE_KEY);
    if (cached) {
      if (cached.cluster) _cluster.ausAblage(cached.cluster);
      if (cached.router) _router.ausAblage(cached.router);
      if (cached.links) _links.ausAblage(cached.links);
      if (cached.knoten) _knoten.ausAblage(cached.knoten);
      if (cached.kanten) _kanten = cached.kanten;
    }
    void ladeTeile();
    _unsub.push(
      abonniere("netz.*.cluster", { rate: "normal" }, { stand: beiCluster, weg: beiClusterWeg }),
      abonniere("openwrt.*.stand", { rate: "normal" }, { stand: beiRouter, weg: beiRouterWeg }),
      abonniere("link.*.stand", { rate: "normal" }, { stand: beiLink, weg: beiLinkWeg }),
      abonniere("topologie.*.knoten", { rate: "normal" }, { stand: beiKnoten, weg: beiKnotenWeg }),
      abonniere("topologie.kanten", { rate: "normal" }, { stand: beiKanten }),
      abonniere("netz.lage", { rate: "normal" }, { stand: beiLage }),
    );
    // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reißt die Leitung ab -- und
    // jeder Deploy tut das --, sind verpasste Löschungen weg. registerRevalidator
    // holt beim Reconnect und beim Zurückkommen des Tabs alles frisch.
    _unsub.push(registerRevalidator(() => void ladeTeile()));
  }
  let aktiv = true;
  return () => {
    if (!aktiv) return;
    aktiv = false;
    _nutzer--;
    if (_nutzer === 0) stoppe();
  };
}

function stoppe(): void {
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = undefined;
  }
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _gescheitert.clear();
  _cluster.leere();
  _router.leere();
  _links.leere();
  _knoten.leere();
  _kanten = undefined;
  _rekey = undefined;
  _integrationen = undefined;
  _kantenNr = 0;
  _rekeyNr = 0;
  _integrationenNr = 0;
  _nr = 0;
}

/** Die Cluster mit Rechnern und Netzgeräten (wie `GET /v2-api/network/topology`);
 *  `undefined`, solange weder Ablage noch Abruf noch Protokoll etwas geliefert haben. */
export function getNetzCluster(): NetworkTopology[] | undefined {
  return _cluster.bekannt ? _cluster.liste.items : undefined;
}

/** Die OpenWrt-Router (wie `GET /v2-api/openwrt/agents`); `undefined`, solange nichts bekannt ist. */
export function getNetzRouter(): OpenwrtSnapshot[] | undefined {
  return _router.bekannt ? _router.liste.items : undefined;
}

/** Die Verbindungszustände (wie `GET /v2-api/link-state`); `undefined`, solange nichts bekannt ist. */
export function getNetzLinks(): LinkState[] | undefined {
  return _links.bekannt ? _links.liste.items : undefined;
}

// Der Graph der Seite; vorhanden, sobald Knoten und Kanten bekannt sind.
// `nodes` ist die Liste selbst (in-place aktualisiert).
const _graph = $derived.by((): TopoGraph | undefined => {
  return _knoten.bekannt && _kanten ? graphAusProtokoll(_knoten.liste.items, _kanten) : undefined;
});

/** Der Topologie-Graph (wie `GET /v2-api/topology`); `undefined`, solange nichts bekannt ist. */
export function getNetzGraph(): TopoGraph | undefined {
  return _graph;
}

/** Die offenen Rekey-Fenster (wie `GET /v2-api/security/rekey-window`); `undefined`, solange nichts bekannt ist. */
export function getNetzRekey(): RekeyWindow[] | undefined {
  return _rekey;
}

/** Die Integrationen (wie `GET /v2-api/integrations`); `undefined`, solange nichts bekannt ist. */
export function getNetzIntegrationen(): Integration[] | undefined {
  return _integrationen;
}

/** Holt die genannten Teile sofort per REST -- nach einer Aktion, deren Ergebnis
 *  die Seite gleich zeigen soll (das Protokoll braucht einen Moment). */
export function refreshNetz(teile: NetzTeil[]): Promise<void> {
  // Ohne Nutzer (Seite verlassen, Aktion endet später) nichts mehr füllen.
  if (_nutzer === 0) return Promise.resolve();
  return ladeTeile(teile);
}
